<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  AiFeature,
  PermissionOrganization,
  OrganizationSettingsViewSetAiFeatureMutation,
  OrganizationSettingsViewSetAiFeatureMutationVariables,
} from '@/generated/graphql'
import { currentOrganization } from '@/app'
import { organizationInfoFragment } from '@/components/layout/organizationFragments'
import useAuthorization from '@/composables/useAuthorization'

const { t } = useI18n()
const { reactiveOrgAuthz } = useAuthorization()
const canEdit = reactiveOrgAuthz(PermissionOrganization.OrgAdmin)

const activeTab = ref('ai')

const setAiFeatureMutation = useMutation<
  OrganizationSettingsViewSetAiFeatureMutation,
  OrganizationSettingsViewSetAiFeatureMutationVariables
>(gql`
  mutation OrganizationSettingsViewSetAiFeature($command: SetOrganizationAiFeature!) {
    account {
      setOrganizationAiFeature(command: $command) {
        ...OrganizationInfo
      }
    }
  }
  ${organizationInfoFragment}
`)
const setAiFeature = async (feature: AiFeature, enabled: boolean | null) => {
  const result = await setAiFeatureMutation.mutate({
    command: { feature, enabled: !!enabled },
  })
  const organization = result?.data?.account.setOrganizationAiFeature
  if (organization) {
    currentOrganization.value = organization
  }
}
</script>

<template>
  <h1>{{ t('view.organization.settings.title') }}</h1>

  <v-tabs v-model="activeTab" class="mb-4">
    <v-tab value="ai">{{ t('view.organization.settings.tabAi') }}</v-tab>
  </v-tabs>

  <template v-if="activeTab === 'ai'">
    <p class="mb-4">{{ t('view.organization.settings.aiExplanation') }}</p>
    <v-checkbox
      v-for="aiFeature in currentOrganization?.aiFeatures || []"
      :key="aiFeature.feature"
      :model-value="aiFeature.enabled"
      :label="t(`aiFeature.${aiFeature.feature}`)"
      :disabled="!canEdit || setAiFeatureMutation.loading.value"
      density="compact"
      hide-details
      @update:model-value="(v) => setAiFeature(aiFeature.feature, v)"
    />
  </template>
</template>
